import React from 'react';
import Logo from '../Logo';

const NAV_LINKS = [
  { label: 'Terms of Service', href: '/terms' },
  { label: 'Trust & Safety', href: '/trust-and-safety' },
];

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#f9f9fb]">
      {/* Nav */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/">
            <Logo width="208" height="32" textFill="black" />
          </a>
          <div className="flex items-center gap-6">
            {NAV_LINKS.map(l => (
              <a key={l.href} href={l.href} className="hidden md:inline text-gray-600 text-sm font-medium hover:text-[#055EFD] transition-colors">{l.label}</a>
            ))}
            <a href="/" className="text-gray-600 text-sm font-medium hover:text-[#055EFD] transition-colors">Home</a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 text-center font-['Montserrat']">Privacy Policy</h1>
          <p className="text-center text-gray-500 text-sm mb-2">Remote Assistant Developer Platform</p>
          <div className="text-center text-gray-400 text-xs mb-12 space-y-1">
            <p><strong>Effective Date:</strong> April 1, 2026</p>
            <p><strong>Last Updated:</strong> April 18, 2026</p>
          </div>

          <div className="space-y-10 text-gray-700 text-sm leading-relaxed">

            <Section n="1" title="Introduction">
              <p>
                This Privacy Policy describes how Remote Assistant ("we," "us," "our") collects, uses, and protects information when you use the Remote Assistant Developer Platform, including the API, developer dashboard, documentation, and related services (collectively, the "Platform").
              </p>
              <p>
                This policy covers two categories of data: (1) <strong>Developer Data</strong> &mdash; information about you as a developer using our Platform, and (2) <strong>End-User Data</strong> &mdash; information about the people who interact with AI agents you deploy using our Platform.
              </p>
            </Section>

            <Section n="2" title="AI System Disclosure">
              <p>
                In accordance with the EU AI Act and emerging global AI transparency requirements, we disclose that this Platform utilizes artificial intelligence systems, including large language models (LLMs), speech-to-text (STT), and text-to-speech (TTS) engines, to process communications.
              </p>
              <p>
                As a developer deploying AI agents on our Platform, you are responsible for ensuring that end users are informed they are interacting with an automated system, as required by applicable law in the jurisdictions where your agents operate.
              </p>
            </Section>

            <Section n="3" title="Developer Data We Collect">
              <p>We collect the following information from developers:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Account Information:</strong> Name, email address, company name, and authentication credentials (via Cognito)</li>
                <li><strong>Billing Information:</strong> Payment method details (processed and stored by Stripe; we do not store card numbers)</li>
                <li><strong>Usage Data:</strong> API call logs, telephony minutes consumed, feature usage, and dashboard activity</li>
                <li><strong>Configuration Data:</strong> Assistant configurations, system prompts, business rules, FAQ content, and module settings</li>
                <li><strong>Technical Data:</strong> IP addresses, browser type, device information, and access timestamps</li>
                <li><strong>Support Data:</strong> Communications with our support team, feedback, and bug reports</li>
              </ul>
            </Section>

            <Section n="4" title="End-User Data Processed Through the Platform">
              <p>When end users interact with AI agents you deploy, the following data may be processed:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Voice Data:</strong> Audio streams from phone calls, processed in real time for speech recognition (not persistently stored as audio)</li>
                <li><strong>Conversation Content:</strong> Transcribed text from voice calls, SMS messages, and email content</li>
                <li><strong>Contact Information:</strong> Phone numbers (caller ID), email addresses, and names provided during interactions</li>
                <li><strong>Interaction Metadata:</strong> Call timestamps, duration, channel type, and session identifiers</li>
                <li><strong>Business Data:</strong> Appointment details, booking information, and other data collected per your assistant configuration</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
                <p className="text-blue-800 text-xs font-medium">
                  <strong>Important:</strong> As the developer deploying AI agents, you are the data controller for end-user data processed through your agents. Remote Assistant acts as a data processor on your behalf. You are responsible for maintaining a privacy policy that covers your use of the Platform and obtaining any required end-user consents.
                </p>
              </div>
            </Section>

            <Section n="5" title="Google User Data">
              <p>
                Remote Assistant's use and transfer to any other app of information received from Google APIs will adhere to the{' '}
                <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-[#055EFD] hover:underline">Google API Services User Data Policy</a>, including the Limited Use requirements.
              </p>

              <h3 className="font-bold text-gray-800 text-sm mt-4 mb-2">5.1 Data Accessed</h3>
              <p>When you connect your Google account, we request access to the following data depending on the integrations you enable:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Google Calendar</strong> (calendar scope): We read and write calendar events to manage appointment bookings, check staff availability, and synchronize scheduling for your AI assistant.</li>
                <li><strong>Google Sheets</strong> (drive.file scope): We read and write to spreadsheets that we create on your behalf for data export, reporting, and business record-keeping. Access is limited to files our application creates.</li>
                <li><strong>Gmail</strong> (gmail.send, gmail.readonly scopes): We send emails on your behalf (e.g., booking confirmations, follow-ups) and read incoming emails to enable your AI assistant to respond to customer inquiries via email.</li>
              </ul>

              <h3 className="font-bold text-gray-800 text-sm mt-4 mb-2">5.2 How We Use Google Data</h3>
              <p>Google user data is used exclusively to provide the features you have enabled:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Calendar data is used to check real-time availability and create or modify bookings on behalf of your end users.</li>
                <li>Sheets data is used to export and organize business records you have requested.</li>
                <li>Drive access is limited to files our application creates for your account.</li>
                <li>Gmail access is used to send transactional emails (booking confirmations, reminders) and to read inbound customer emails so your AI assistant can respond.</li>
              </ul>
              <p className="mt-2">
                <strong>We do not use Google user data for advertising, market research, or any purpose unrelated to providing and improving our core AI assistant functionality for your account.</strong>
              </p>

              <h3 className="font-bold text-gray-800 text-sm mt-4 mb-2">5.3 How We Store Google Data</h3>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>OAuth tokens (access and refresh tokens) are stored encrypted in AWS DynamoDB with AES-256 encryption at rest and are accessible only to your account.</li>
                <li>Calendar event data is cached temporarily (up to 24 hours) in Redis for real-time availability checks, then automatically expires.</li>
                <li>We do not persistently store the contents of your emails or full calendar event details beyond what is required for active assistant operations.</li>
              </ul>

              <h3 className="font-bold text-gray-800 text-sm mt-4 mb-2">5.4 How We Share Google Data</h3>
              <p>We do not sell, rent, or share your Google user data with third parties except:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>AI Processing:</strong> Calendar availability and email content may be provided to our AI language model providers (OpenAI, Groq) as context during active conversations, solely to generate relevant responses. This data is not retained by these providers for training purposes.</li>
                <li><strong>Infrastructure Providers:</strong> Data is processed through AWS (hosting, database), Redis Cloud (caching), and Deepgram (speech-to-text) as necessary sub-processors listed in Section 8.</li>
                <li><strong>Legal Requirements:</strong> We may disclose data if required by law, regulation, or valid legal process.</li>
              </ul>
              <p className="mt-2">
                <strong>We do not share Google user data with any other third parties, and we do not use it for purposes beyond those described in this policy.</strong>
              </p>

              <h3 className="font-bold text-gray-800 text-sm mt-4 mb-2">5.5 Revoking Google Access</h3>
              <p>
                You can disconnect your Google account at any time from your developer dashboard. You can also revoke access directly from your{' '}
                <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-[#055EFD] hover:underline">Google Account permissions page</a>. Upon revocation, we delete your stored OAuth tokens and cease all access to your Google data. Cached data expires automatically within 24 hours.
              </p>
            </Section>

            <Section n="6" title="How We Use Information">
              <p><strong>Developer Data</strong> is used to:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Provide, operate, and maintain the Platform</li>
                <li>Process billing and enforce usage limits</li>
                <li>Communicate service updates, security alerts, and billing notifications</li>
                <li>Provide technical support</li>
                <li>Improve Platform performance, reliability, and features</li>
                <li>Detect and prevent fraud, abuse, and security threats</li>
              </ul>
              <p className="mt-3"><strong>End-User Data</strong> is used exclusively to:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Provide the AI agent services you configured (voice processing, conversation management, business logic execution)</li>
                <li>Generate conversation logs accessible in your developer dashboard</li>
                <li>Enable real-time features such as live call monitoring and whisper guidance</li>
              </ul>
            </Section>

            <Section n="7" title="Data Sovereignty & AI Training">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-bold text-green-900 text-sm mb-2">The No-Global-Training Guarantee</h3>
                <p className="text-green-800 text-xs">
                  We do <strong>not</strong> use your proprietary business data, assistant configurations, end-user conversations, voice recordings, or Google user data to train, fine-tune, or improve foundational AI models used across our broader customer base. Your data remains siloed to your account and is used exclusively to provide services to you.
                </p>
              </div>
              <p className="mt-3">
                We may use aggregated, anonymized, and de-identified usage statistics (e.g., average API response times, error rates) to improve Platform infrastructure and performance. Such data cannot be used to identify any individual developer or end user.
              </p>
            </Section>

            <Section n="8" title="Data Sharing & Sub-Processors">
              <p>
                We do not sell personal information. We share data only with the following categories of third parties, solely as necessary to provide the Services:
              </p>
              <div className="overflow-x-auto mt-3">
                <table className="min-w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="p-3 border-b border-gray-200 text-left font-semibold text-gray-700">Sub-Processor</th>
                      <th className="p-3 border-b border-gray-200 text-left font-semibold text-gray-700">Purpose</th>
                      <th className="p-3 border-b border-gray-200 text-left font-semibold text-gray-700">Data Location</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr><td className="p-3 font-medium">Amazon Web Services (AWS)</td><td className="p-3">Infrastructure, compute, storage, database</td><td className="p-3">US (us-east-1)</td></tr>
                    <tr><td className="p-3 font-medium">OpenAI</td><td className="p-3">LLM inference, text-to-speech</td><td className="p-3">US</td></tr>
                    <tr><td className="p-3 font-medium">Groq</td><td className="p-3">LLM inference (low-latency)</td><td className="p-3">US</td></tr>
                    <tr><td className="p-3 font-medium">Deepgram</td><td className="p-3">Speech-to-text transcription</td><td className="p-3">US</td></tr>
                    <tr><td className="p-3 font-medium">Cartesia</td><td className="p-3">Text-to-speech synthesis</td><td className="p-3">US</td></tr>
                    <tr><td className="p-3 font-medium">Google Cloud</td><td className="p-3">Text-to-speech synthesis</td><td className="p-3">US</td></tr>
                    <tr><td className="p-3 font-medium">Twilio</td><td className="p-3">PSTN telephony, SMS delivery</td><td className="p-3">US</td></tr>
                    <tr><td className="p-3 font-medium">Telnyx</td><td className="p-3">PSTN telephony, SMS delivery</td><td className="p-3">US</td></tr>
                    <tr><td className="p-3 font-medium">Daily.co</td><td className="p-3">WebRTC audio transport</td><td className="p-3">US</td></tr>
                    <tr><td className="p-3 font-medium">Stripe</td><td className="p-3">Payment processing, billing</td><td className="p-3">Global</td></tr>
                    <tr><td className="p-3 font-medium">Redis Cloud</td><td className="p-3">Real-time session state, caching</td><td className="p-3">US</td></tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-3">
                We will notify developers of material changes to our sub-processor list via email at least 30 days in advance.
              </p>
            </Section>

            <Section n="9" title="Data Retention">
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Conversation transcripts:</strong> Retained for 90 days by default, accessible via the developer dashboard. You may configure shorter retention periods or request deletion at any time.</li>
                <li><strong>Voice audio:</strong> Processed in real time for transcription; raw audio is <strong>not</strong> persistently stored.</li>
                <li><strong>API logs:</strong> Retained for 30 days for debugging and support purposes.</li>
                <li><strong>Account data:</strong> Retained for the duration of your account plus 30 days after termination.</li>
                <li><strong>Billing records:</strong> Retained as required by applicable tax and accounting regulations.</li>
                <li><strong>Google user data:</strong> OAuth tokens are retained while the integration is active. Cached calendar and email data expires automatically within 24 hours. All Google data is deleted upon revocation of access.</li>
              </ul>
            </Section>

            <Section n="10" title="Data Security">
              <p>We implement industry-standard security measures including:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Encryption in transit:</strong> All API and dashboard traffic uses TLS 1.2+</li>
                <li><strong>Encryption at rest:</strong> Data stored in AWS is encrypted using AES-256</li>
                <li><strong>Access control:</strong> Role-based access with multi-factor authentication for internal systems</li>
                <li><strong>API authentication:</strong> API key authentication with per-key rate limiting and usage tracking</li>
                <li><strong>Network isolation:</strong> Developer environments are logically isolated; Redis key namespaces and DynamoDB partitions ensure data separation</li>
                <li><strong>Monitoring:</strong> Real-time security monitoring and anomaly detection on all infrastructure</li>
              </ul>
            </Section>

            <Section n="11" title="Your Rights">
              <p>Depending on your jurisdiction, you and your end users may have the following rights:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Access:</strong> Request a copy of personal data we hold</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of personal data (subject to legal retention requirements)</li>
                <li><strong>Portability:</strong> Request data export in a machine-readable format</li>
                <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              </ul>
              <p className="mt-3">
                For developer account requests, use the developer dashboard or contact <a href="mailto:privacy@remote-assistant.io" className="text-[#055EFD] hover:underline">privacy@remote-assistant.io</a>. For end-user requests, developers should handle these directly and may use our API to facilitate data access or deletion.
              </p>
            </Section>

            <Section n="12" title="International Data Transfers">
              <p>
                The Platform infrastructure is hosted in the United States (AWS us-east-1). If you or your end users are located outside the US, data will be transferred to and processed in the US. We rely on Standard Contractual Clauses (SCCs) and other approved transfer mechanisms to ensure adequate data protection for international transfers.
              </p>
            </Section>

            <Section n="13" title="Children's Privacy">
              <p>
                The Platform is not intended for use by children under 13 (or the applicable age of digital consent in your jurisdiction). We do not knowingly collect personal information from children. If you become aware that a child has provided personal information through an AI agent you deployed, please delete the data and contact us.
              </p>
            </Section>

            <Section n="14" title="Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. Material changes will be communicated via email to the address associated with your developer account at least 30 days before taking effect. The "Last Updated" date at the top of this page indicates when this policy was most recently revised.
              </p>
            </Section>

            <Section n="15" title="Contact">
              <p>For privacy-related questions or requests:</p>
              <ul className="list-none space-y-1 mt-2">
                <li><strong>Privacy:</strong> <a href="mailto:privacy@remote-assistant.io" className="text-[#055EFD] hover:underline">privacy@remote-assistant.io</a></li>
                <li><strong>Legal:</strong> <a href="mailto:legal@remote-assistant.io" className="text-[#055EFD] hover:underline">legal@remote-assistant.io</a></li>
                <li><strong>DPO:</strong> <a href="mailto:dpo@remote-assistant.io" className="text-[#055EFD] hover:underline">dpo@remote-assistant.io</a></li>
              </ul>
            </Section>

          </div>
        </div>
      </main>

      <footer className="py-8 text-center text-gray-400 text-xs border-t border-gray-200">
        &copy; 2026 Remote Assistant. Compliant with EU AI Act, GDPR, and CCPA standards.
      </footer>
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <div>
    <h2 className="text-lg font-bold text-gray-900 mb-3 font-['Montserrat']">
      {n}. {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </div>
);

export default PrivacyPolicy;
